import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";

const NOTO_TTF_URL =
  "https://cdn.jsdelivr.net/gh/googlefonts/noto-fonts@main/hinted/ttf/NotoSans/NotoSans-Regular.ttf";
const FONT_NAME = "NotoSans";
const HEADER_FILL = [153, 27, 27];

let fontBase64Promise = null;

function arrayBufferToBase64(buffer) {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

async function loadNotoFontBase64() {
  if (!fontBase64Promise) {
    fontBase64Promise = fetch(NOTO_TTF_URL)
      .then((r) => {
        if (!r.ok) throw new Error(`Font HTTP ${r.status}`);
        return r.arrayBuffer();
      })
      .then(arrayBufferToBase64);
  }
  return fontBase64Promise;
}

function formatYearMonthVi(ym) {
  if (!ym || !/^\d{4}-\d{2}$/.test(ym)) return null;
  const [y, m] = ym.split("-");
  return `Tháng ${Number(m)}/${y}`;
}

function periodLabel(meta = {}) {
  const ym = formatYearMonthVi(meta.year_month);
  if (ym) return ym;
  if (meta.date_from) {
    return `Từ ${meta.date_from}${meta.date_to ? ` đến ${meta.date_to}` : ""}`;
  }
  return "Toàn bộ thời gian";
}

async function createPdfDoc(orientation = "landscape") {
  const doc = new jsPDF({
    orientation,
    unit: "mm",
    format: "a4",
  });

  let font = "helvetica";
  try {
    const fontBase64 = await loadNotoFontBase64();
    doc.addFileToVFS("NotoSans-Regular.ttf", fontBase64);
    doc.addFont("NotoSans-Regular.ttf", FONT_NAME, "normal");
    doc.setFont(FONT_NAME, "normal");
    font = FONT_NAME;
  } catch (e) {
    console.warn("Không tải được font Noto, PDF có thể lỗi dấu tiếng Việt:", e);
  }

  return { doc, font };
}

function addPageNumbers(doc, footerText) {
  const pageCount = doc.getNumberOfPages();
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.text(`Trang ${i}/${pageCount} — ${footerText}`, pageW / 2, pageH - 6, {
      align: "center",
    });
  }
}

function drawReportHeader(doc, { title, lines = [], marginX = 10 }) {
  const pageW = doc.internal.pageSize.getWidth();
  let y = 12;
  doc.setFontSize(13);
  doc.text(title, pageW / 2, y, { align: "center" });
  y += 7;
  doc.setFontSize(9);
  lines.filter(Boolean).forEach((line) => {
    doc.text(line, marginX, y, { maxWidth: pageW - marginX * 2 });
    y += 5;
  });
  return y + 2;
}

function tableStyles(font) {
  return {
    styles: {
      font,
      fontSize: 7.5,
      cellPadding: 1.4,
      overflow: "linebreak",
      valign: "middle",
    },
    headStyles: {
      font,
      fontStyle: "normal",
      fillColor: HEADER_FILL,
      textColor: 255,
      fontSize: 7.5,
    },
  };
}

/**
 * Xuất báo cáo điểm danh ra PDF (A4 ngang, font tiếng Việt).
 * @returns {Promise<{ ok: boolean, message?: string }>}
 */
export async function exportAttendanceReportToPdf({
  view = "classes",
  records = [],
  absenceRecords = [],
  summary = {},
  meta = {},
  filterLabel = "",
  classInfo = null,
} = {}) {
  if (!records.length) {
    return { ok: false, message: "Không có dữ liệu để xuất PDF." };
  }

  const stamp = new Date().toISOString().slice(0, 10);
  const ym = meta.year_month ? `_${meta.year_month}` : "";
  const exportedAt = new Date().toLocaleString("vi-VN", {
    timeZone: "Asia/Ho_Chi_Minh",
  });
  const period = periodLabel(meta);
  const marginX = 10;
  const isStudents = view === "students";

  const { doc, font } = await createPdfDoc("landscape");
  const ts = tableStyles(font);

  if (isStudents) {
    const classLine = classInfo?.class_name
      ? `Lớp: ${classInfo.class_name}${
          classInfo.class_code ? ` (${classInfo.class_code})` : ""
        }${classInfo.course_name ? ` · Khóa: ${classInfo.course_name}` : ""}`
      : "Chi tiết học viên";

    let y = drawReportHeader(doc, {
      title: "BÁO CÁO ĐIỂM DANH — CHI TIẾT HỌC VIÊN",
      lines: [
        classLine,
        `Kỳ báo cáo: ${period}${filterLabel ? ` · ${filterLabel}` : ""}`,
        `Học viên: ${summary.students ?? records.length} · Buổi: ${
          summary.sessions ?? 0
        } · Có mặt: ${summary.present ?? 0} · Muộn: ${summary.late ?? 0} · Vắng: ${
          summary.absent ?? 0
        }`,
        `Xuất lúc: ${exportedAt}`,
      ],
      marginX,
    });

    doc.setFontSize(9);
    doc.text("1. Tổng hợp chuyên cần học viên", marginX, y);
    y += 2;

    autoTable(doc, {
      startY: y,
      margin: { left: marginX, right: marginX },
      head: [
        [
          "STT",
          "Mã HV",
          "Họ tên",
          "Có mặt",
          "Muộn",
          "Vắng",
          "Vắng CP",
          "Vắng KP",
          "Tổng buổi",
          "Chuyên cần (%)",
        ],
      ],
      body: records.map((r, idx) => [
        String(idx + 1),
        r.student_code || "",
        r.student_name || "",
        String(r.present_count ?? 0),
        String(r.late_count ?? 0),
        String(r.absent_count ?? 0),
        String(r.absent_excused_count ?? 0),
        String(r.absent_unexcused_count ?? 0),
        String(r.total_sessions ?? 0),
        `${Number(r.attendance_rate) || 0}%`,
      ]),
      ...ts,
      columnStyles: {
        0: { cellWidth: 8, halign: "center" },
        1: { cellWidth: 16, halign: "center" },
        2: { cellWidth: 42 },
        3: { cellWidth: 14, halign: "center" },
        4: { cellWidth: 12, halign: "center" },
        5: { cellWidth: 12, halign: "center" },
        6: { cellWidth: 14, halign: "center" },
        7: { cellWidth: 14, halign: "center" },
        8: { cellWidth: 14, halign: "center" },
        9: { cellWidth: 18, halign: "center" },
      },
    });

    y = (doc.lastAutoTable?.finalY || y) + 8;
    const pageH = doc.internal.pageSize.getHeight();
    if (y > pageH - 40) {
      doc.addPage();
      y = 16;
    }

    const absenceRows =
      absenceRecords.length > 0
        ? absenceRecords
        : records.filter((r) => Number(r.absent_count) > 0);

    doc.setFont(font, "normal");
    doc.setFontSize(9);
    doc.text("2. Chi tiết vắng mặt theo ngày", marginX, y);
    y += 2;

    autoTable(doc, {
      startY: y,
      margin: { left: marginX, right: marginX },
      head: [
        [
          "STT",
          "Mã HV",
          "Họ tên",
          "Tổng vắng",
          "Vắng CP",
          "Vắng KP",
          "Chi tiết ngày vắng",
          "Ghi chú GV",
        ],
      ],
      body: absenceRows.length
        ? absenceRows.map((r, idx) => [
            String(idx + 1),
            r.student_code || "",
            r.student_name || "",
            String(r.absent_count ?? 0),
            String(r.absent_excused_count ?? 0),
            String(r.absent_unexcused_count ?? 0),
            r.absence_summary_text || "",
            r.absence_teacher_notes || "",
          ])
        : [["—", "Không có học viên vắng mặt trong kỳ", "", "", "", "", "", ""]],
      ...ts,
      columnStyles: {
        0: { cellWidth: 8, halign: "center" },
        1: { cellWidth: 16, halign: "center" },
        2: { cellWidth: 32 },
        3: { cellWidth: 14, halign: "center" },
        4: { cellWidth: 14, halign: "center" },
        5: { cellWidth: 14, halign: "center" },
        6: { cellWidth: 62 },
        7: { cellWidth: 48 },
      },
    });

    doc.setFontSize(7);
    doc.text(
      "Ghi chú: CP = vắng có phép, KP = vắng không phép. Tỷ lệ chuyên cần = (Có mặt + Muộn) / Tổng buổi × 100.",
      marginX,
      doc.internal.pageSize.getHeight() - 12,
      { maxWidth: doc.internal.pageSize.getWidth() - marginX * 2 }
    );

    addPageNumbers(
      doc,
      `Báo cáo điểm danh ${classInfo?.class_code || "lớp"} ${period}`
    );

    doc.save(`bao_cao_dd_${classInfo?.class_code || "lop"}${ym}_${stamp}.pdf`);
    return { ok: true };
  }

  let y = drawReportHeader(doc, {
    title: "BÁO CÁO ĐIỂM DANH — TỔNG HỢP THEO LỚP",
    lines: [
      `Kỳ báo cáo: ${period}${filterLabel ? ` · ${filterLabel}` : ""}`,
      `Số lớp: ${summary.classes ?? records.length} · Học viên: ${
        summary.students ?? 0
      } · Buổi: ${summary.sessions ?? 0} · Có mặt: ${summary.present ?? 0} · Muộn: ${
        summary.late ?? 0
      } · Vắng: ${summary.absent ?? 0}`,
      `Xuất lúc: ${exportedAt}`,
    ],
    marginX,
  });

  autoTable(doc, {
    startY: y,
    margin: { left: marginX, right: marginX },
    head: [
      [
        "STT",
        "Lớp",
        "Mã lớp",
        "Khóa học",
        "Chi nhánh",
        "Số HV",
        "Số buổi",
        "Có mặt",
        "Muộn",
        "Vắng",
        "Chuyên cần (%)",
      ],
    ],
    body: records.map((r, idx) => [
      String(idx + 1),
      r.class_name || "",
      r.class_code || "",
      r.course_name || "",
      r.branch_name || "",
      String(r.student_count ?? 0),
      String(r.session_count ?? 0),
      String(r.present_count ?? 0),
      String(r.late_count ?? 0),
      String(r.absent_count ?? 0),
      `${Number(r.attendance_rate) || 0}%`,
    ]),
    ...ts,
    columnStyles: {
      0: { cellWidth: 8, halign: "center" },
      1: { cellWidth: 36 },
      2: { cellWidth: 18 },
      3: { cellWidth: 32 },
      4: { cellWidth: 24 },
      5: { cellWidth: 12, halign: "center" },
      6: { cellWidth: 14, halign: "center" },
      7: { cellWidth: 14, halign: "center" },
      8: { cellWidth: 12, halign: "center" },
      9: { cellWidth: 12, halign: "center" },
      10: { cellWidth: 18, halign: "center" },
    },
  });

  addPageNumbers(doc, `Báo cáo điểm danh theo lớp ${period}`);

  doc.save(`bao_cao_diem_danh_lop${ym}_${stamp}.pdf`);
  return { ok: true };
}
